import {
    HttpErrorResponse,
    HttpEvent,
    HttpEventType,
    HttpHandlerFn,
    HttpInterceptorFn,
    HttpRequest
} from '@angular/common/http';
import { ErrorHandler, inject } from '@angular/core';
import { CommonService } from '@common/services/common.service';
import { LanguageIds } from '@common/services/i18n.service';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { BehaviorSubject, NEVER, throwError } from 'rxjs';
import { catchError, filter, mergeMap, switchMap, take } from 'rxjs/operators';

// Shared state for the interceptor (since we can't use class properties)
let activeRequests = 0;
let refreshingToken = false;
const refreshTokenSubject = new BehaviorSubject<any>(null);
let loadingBar: any = null;

export const appHttpInterceptor: HttpInterceptorFn = (req: HttpRequest<any>, next: HttpHandlerFn): any => {
    // Inject dependencies
    const errorHandler = inject(ErrorHandler);
    const loadingBarService = inject(LoadingBarService);
    const commonService = inject(CommonService);

    if (req.url.includes('/assets/i18n/')) {
        return next(req);
    }

    // Initialize loading bar if not already done
    if (!loadingBar) {
        loadingBar = loadingBarService.useRef('http');
    }

    loadingBar.start();

    req = setHeaders(req, commonService);
    activeRequests++;

    return next(req).pipe(
        mergeMap((event: HttpEvent<any>) => {
            if (event.type === HttpEventType.Response) {
                decreaseActiveRequests();
            }
            return [event];
        }),
        catchError((error) => {
            decreaseActiveRequests();

            if (shouldRefreshToken(error)) {
                return refreshToken(req, next, commonService);
            }

            if (isForbidden(error) && !Object.prototype.hasOwnProperty.call(req.body, 'isPrefetch')) {
                commonService.dialogService
                    .confirm({
                        options: {
                            title: 'Unauthorized',
                            message: error.error.errorMessage,
                            hideCancel: true
                        },
                        dialogSettings: { width: '30vw' }
                    })
                    .then(() => commonService.router.navigate(['/home']));
                return NEVER;
            }

            if (shouldLogout(error)) {
                // In case the user logged out from another window
                commonService.userService.logout();
                return NEVER;
            }

            if (error?.error?.validationErrors || error?.error?.entityErrors) {
                errorHandler.handleError(error);
            }
            return throwError(() => error);
        })
    );
};

// Helper functions (moved outside the interceptor function)
function shouldRefreshToken(error: any): boolean {
    return (
        error instanceof HttpErrorResponse &&
        !error.url.endsWith('RefreshAccessToken') &&
        error.headers.has('Token-Expired') &&
        error.status === 401
    );
}

function isForbidden(error: any): boolean {
    return error instanceof HttpErrorResponse && error.status === 403;
}

function shouldLogout(error: any): boolean {
    return (
        error instanceof HttpErrorResponse &&
        !error.url.endsWith('RefreshAccessToken') &&
        !error.url.endsWith('Login') &&
        !error.headers.has('Token-Expired') &&
        error.status === 401
    );
}

function refreshToken(request: HttpRequest<any>, next: any, commonService: CommonService) {
    if (!refreshingToken) {
        refreshingToken = true;
        refreshTokenSubject.next(null);

        return commonService.userService.refreshAccessToken().pipe(
            switchMap((token: any) => {
                const currentUser = commonService.userService.currentUserSubject.getValue();
                // If currentUser is not set, redirect is used to activate AuthGuard again
                if (!currentUser) {
                    commonService.router.navigateByUrl('/');
                }
                refreshingToken = false;
                refreshTokenSubject.next(token.accessToken);
                return next(setHeaders(request, commonService));
            }),
            catchError((error) => {
                refreshingToken = false;
                // logout only when the refresh token is invalid
                if ([400, 401].includes(error.status)) {
                    commonService.userService.logout();
                }
                return throwError(() => error);
            })
        );
    }

    return refreshTokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap(() => next(setHeaders(request, commonService)))
    );
}

function setHeaders(request: HttpRequest<any>, commonService: CommonService): HttpRequest<any> {
    return request.clone({
        setHeaders: {
            Language: commonService.rememberStateService.get<LanguageIds>('languageId') || ''
        }
    });
}

function decreaseActiveRequests(): void {
    activeRequests--;

    if (activeRequests === 0 && loadingBar) {
        loadingBar.complete();
    }
}
